<template>
  <v-container id="users_root" fluid>
    <!-- DIALOG -->
    <v-row justify="center" align="center">
      <v-dialog v-model="showDialog" persistent max-width="496">
        <create-or-update-user
          :key="chosenUserId"
          :user-id="chosenUserId"
          @finished="handleDialog"
        ></create-or-update-user>
      </v-dialog>
    </v-row>
    <!-- CONTENT -->
    <v-row class="mb-6 px-6" align="center" justify="center">
      <v-col cols="7">
        <span class="text-h5">{{ $t('users.title.list') }}</span>
      </v-col>

      <v-col class="d-flex flex-row">
        <v-btn id="create_user" class="align-self-end mr-8" color="primary" @click="showNewUserDialog">
          <v-icon left>mdi-plus-circle-outline</v-icon>
          {{ $t('users.content.createUser') }}
        </v-btn>
        <v-text-field
          id="user_search"
          v-model="search"
          :label="$t('dataTable.search')"
          append-icon="mdi-magnify"
          single-line
          hide-details
          clearable
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row class="mb-4">
      <v-btn v-if="userGroupsEnabled" id="manage_groups" outlined class="pl-1 ml-4" :to="{name: 'UserGroups'}">
        <v-icon class="mx-1">mdi-checkbox-multiple-blank-circle-outline</v-icon>
        {{ $t('users.manageGroupsBtn') }}
      </v-btn>

      <central-key-activation-modal>
      </central-key-activation-modal>
    </v-row>
    <v-card>
      <v-data-table
        must-sort
        :headers="headers"
        :items="userList"
        :options.sync="pagination"
        :server-items-length="totalItems"
        :loading="dataTableLoading"
        :loading-text="$t('dataTable.content.loadingPleaseWait')"
        :no-data-text="$t('dataTable.content.noDataAvailable')"
        :footer-props="{
          'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
          'page-text': $t('dataTable.footer.pageText'),
          'items-per-page-options': [10,25,50],
        }"
      >
        <template #[`item.status`]="{ item }">
          <v-chip label class="text--darken-1" :class="getChipColor(item.status)">
            {{ $t(`dataTable.content.users.statusEnum.${item.status.toLowerCase()}`) }}
          </v-chip>
        </template>

        <template #[`item.name`]="{item}">
          <span>{{ item.firstName }} {{ item.lastName }} </span>
        </template>

        <template #[`item.role`]="{item}">
          <v-chip-group>
            <v-chip
              v-for="role in item.roles"
              :key="role"
            >
              {{ getRoleChipText(role) }}
            </v-chip>
            <v-chip v-if="item.roles.length ===0 "> {{ $t("users.content.roles.spectator") }}</v-chip>
          </v-chip-group>
        </template>

        <template #[`item.createdAt`]="{ item }">
          <span class="font-italic caption">{{ convertDate(item.createdAt) }}</span>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn id="edit_user" class="pa-1 " text @click="showUpdateDialog(item.id)">
            <v-icon>mdi-cog-outline</v-icon>
            {{ $t('dataTable.content.action.update') }}
          </v-btn>
          <v-btn id="delete_user" class="pa-1 " text @click="deleteUser(item.id,$event)">
            <v-icon>mdi-delete</v-icon>
            {{ $t("dataTable.content.action.delete") }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import debounce from 'lodash.debounce'
import { mapGetters } from 'vuex'
import Role from '../../Role'
import CentralKeyActivationModal from '../../components/CentralKeyActivationModal.vue'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../event-bus'
import users from '../../services/api/users'
import CommonError from '../CommonError'
import CreateOrUpdateUser from './CreateOrUpdateUser.vue'
import UserStatus from './UserStatus'
import defaultRoles from './defaultRoles'
export default {
  name: 'Users',

  components: {
    CreateOrUpdateUser,
    CentralKeyActivationModal
  },

  data() {
    return {
      search: null,
      pagination: {
        sortBy: ['name']
      },
      userList: [],
      totalItems: 0,
      userGroupsEnabled: this.$store.state.remoteConfig ? this.$store.state.remoteConfig.userGroupsEnabled : false,
      dataTableLoading: false,
      headers: [
        {
          text: this.$t('dataTable.header.users.id'),
          align: 'left',
          value: 'id'
        },
        {
          text: this.$t('dataTable.header.users.name'),
          align: 'left',
          value: 'name'
        },
        {
          text: this.$t('dataTable.header.users.status'),
          align: 'center',
          value: 'status'
        },
        {
          text: this.$t('dataTable.header.users.role'),
          align: 'left',
          value: 'role'
        },
        {
          text: this.$t('dataTable.header.createdAt'),
          align: 'left',
          value: 'createdAt'
        },
        {
          text: this.$t('dataTable.header.users.email'),
          align: 'left',
          value: 'email'
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ],
      // dialog
      showDialog: false,
      chosenUserId: 0

    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchUsersWatcher()
    },

    pagination: {
      handler() {
        this.searchUsers()
      }
    }
  },

  methods: {
    async searchUsers() {
      this.dataTableLoading = true

      const pageRequest = {
        'page-num': this.pagination.page,
        'page-size': this.pagination.itemsPerPage || 10,
        'sort-field': this.pagination.sortBy[0],
        'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
      }

      try {
        const page = await users.listUsers(this.search, pageRequest)
        this.userList = page.content
        this.totalItems = page.total
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.dataTableLoading = false
      }
    },

    searchUsersWatcher: debounce(function() {
      this.searchUsers()
    }, 500),

    getChipColor(status) {
      // todo: legyenek fix szelesek
      switch (status) {
        case UserStatus.ENABLED:
          return 'statusEnabled'
        case UserStatus.SUSPENDED:
          return 'statusInactive'
      }
    },

    getRoleChipText(roleValue) {
      const role = defaultRoles.find(role => role.value === roleValue)
      if (!roleValue) {
        return ' Viever only'
      }
      return role ? this.$t(role.displayTextJsonKey) : 'Viewer only'
    },

    async deleteUser(id, e) {
      const conf = await showConfirmationMenu({ posX: e.clientX, posY: e.clientY })
      if (!conf) {
        return
      }

      try {
        await users.deleteUser(id)
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
        this.searchUsers()
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if ((error.response !== undefined) && (error.response.data.code !== undefined)) {
          const splittedErrorMessage = error.response.data.message.split(' ')
          if (error.response.data.code === CommonError.STILL_REFERENCED) {
            errorMsg = this.$t('toastMessage.stillReferenced', { 0: splittedErrorMessage[0], 1: splittedErrorMessage[5] })
          } else {
            errorMsg = this.getErrorMessage(error)
          }
        } else {
          errorMsg = this.getErrorMessage(error)
        }
        showToastError(errorMsg)
      }
    },

    showNewUserDialog() {
      this.showDialog = true
    },

    showUpdateDialog(e) {
      this.showDialog = true
      this.chosenUserId = e
    },

    handleDialog() {
      this.showDialog = false
      this.chosenUserId = 0
      this.searchUsers()
    }
  }
}
</script>

<style>
</style>
